/* Optional Universal 01 helpers. Turbo alone selects variants and submits carts. */
(() => {
  'use strict';
  if (window.BPLProductUX) { window.BPLProductUX.init(); return; }

  const mainSelector = '.shopify-section--product-template .bpl-product--01';
  const targetSelector = 'details[data-bpl-dimensions-product-id]';
  const states = new Map();
  const unloadingRoots = new WeakSet();
  let frame = 0;
  let clearance;
  let pageObserver;
  let globalListeners = false;

  function writeText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function visible(node) {
    if (!node || node.hidden || node.closest('[hidden]')) return false;
    const style = window.getComputedStyle(node);
    const rect = node.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  function headerOffset() {
    let fixed = 0;
    let fallback = 0;
    document.querySelectorAll('#header, [data-desktop-header], .sticky_nav, .header-fixed--true').forEach(node => {
      const style = window.getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      if (style.display === 'none' || style.visibility === 'hidden' || !rect.width || !rect.height) return;
      if (style.position === 'fixed' || style.position === 'sticky') fixed = Math.max(fixed, rect.height, Math.max(0, rect.bottom));
      else if (node.classList.contains('header-fixed--true')) fallback = Math.max(fallback, rect.height);
    });
    return Math.ceil(fixed || fallback) + 20;
  }

  function goTo(target, focusTarget = target) {
    let ancestor = target;
    while (ancestor) {
      if (ancestor.tagName === 'DETAILS') ancestor.open = true;
      ancestor = ancestor.parentElement;
    }
    if (!focusTarget.hasAttribute('tabindex') && focusTarget.tagName !== 'SUMMARY') focusTarget.tabIndex = -1;
    focusTarget.focus({ preventScroll: true });
    window.scrollTo({
      top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerOffset()),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
    });
  }

  function dimensionsFor(main) {
    // Each full-details section belongs to the preceding main product. Stopping
    // at the next main also prevents a duplicate product instance stealing it.
    const section = main.closest('.shopify-section--product-template');
    const id = main.dataset.bplProductId;
    const own = main.querySelector(targetSelector);
    if (own && own.dataset.bplDimensionsProductId === id) return own;
    let next = section && section.nextElementSibling;
    while (next) {
      if (next.matches('.shopify-section--product-template') || next.querySelector('.shopify-section--product-template')) break;
      const target = !unloadingRoots.has(next) && Array.from(next.querySelectorAll(targetSelector)).find(node => node.dataset.bplDimensionsProductId === id);
      if (target) return target;
      next = next.nextElementSibling;
    }
    return null;
  }

  function selection(state) {
    const master = state.form.querySelector('select[name="id"], input[name="id"]');
    const variant = master && state.variants.find(item => String(item.id) === master.value);
    const nativeLabel = (state.native.querySelector('.text') || state.native).textContent.trim();
    const disabled = state.native.disabled || state.native.classList.contains('disabled') || state.native.getAttribute('aria-disabled') === 'true';
    const busy = state.native.getAttribute('aria-busy') === 'true' || Boolean(state.native.querySelector('.zoomOut, .checkmark-active'));
    const mode = busy ? 'busy' : !variant ? 'choose' : !variant.available ? 'soldout' : disabled ? 'disabled' : 'ready';
    const options = variant && (variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean));
    const label = options && options.length && !(options.length === 1 && /^default title$/i.test(options[0]))
      ? options.join(' · ') : state.form.dataset.productTitle || '';
    // Reuse the currently rendered native amount, including its selected currency.
    const priceNode = state.main.querySelector('[data-price-ui] > [data-price], .modal_price .current_price');
    const price = variant && priceNode ? priceNode.textContent.replace(/\s+/g, ' ').trim() : '';
    return { mode, nativeLabel, label, price };
  }

  function pageObstructed() {
    if (document.body.classList.contains('blocked-scroll') || document.body.classList.contains('fancybox-active') || document.body.classList.contains('trustedsite-body-noscroll')) return true;
    if (Array.from(document.querySelectorAll('.cart-container.active_link, dialog[open], [aria-modal="true"]')).some(visible)) return true;
    const active = document.activeElement;
    if (active && (active.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]), textarea, select') || active.isContentEditable)) return true;
    return Boolean(window.visualViewport && window.visualViewport.scale > 1.1);
  }

  function syncState(state, obstruction) {
    const target = dimensionsFor(state.main);
    state.target = target;
    if (state.link) {
      state.link.hidden = !target;
      if (target) {
        state.link.href = `#${target.id}`;
        state.link.setAttribute('aria-controls', target.id);
      } else {
        state.link.removeAttribute('aria-controls');
      }
    }
    if (!state.bar || !state.native || !state.form || !state.button) return;
    const current = selection(state);
    if (state.previousMode === 'busy' && current.mode !== 'busy') state.stickySubmitted = false;
    state.previousMode = current.mode;
    writeText(state.bar.querySelector('[data-bpl-sticky-variant]'), current.mode === 'choose' ? 'Select your options' : current.label);
    writeText(state.bar.querySelector('[data-bpl-sticky-price]'), current.price);
    writeText(state.button, current.mode === 'choose' ? 'Choose options' : current.mode === 'busy' ? 'Adding…' : current.nativeLabel || 'Add to cart');
    state.button.disabled = !['ready', 'choose'].includes(current.mode);
    state.button.setAttribute('aria-busy', String(current.mode === 'busy'));
    const rect = state.native.getBoundingClientRect();
    const show = window.matchMedia('(max-width: 798px)').matches && visible(state.native) && rect.bottom <= 0 && !obstruction;
    state.bar.hidden = !show;
  }

  function sync() {
    frame = 0;
    const obstruction = pageObstructed();
    let height = 0;
    let shown = false;
    states.forEach(state => {
      if (!state.main.isConnected) { detach(state.main); return; }
      syncState(state, obstruction);
      if (state.bar && !state.bar.hidden) {
        // One viewport has one active purchase bar. Later product sections win.
        if (shown) shown.hidden = true;
        shown = state.bar;
        height = state.bar.getBoundingClientRect().height;
      }
    });
    if (!clearance && height) {
      clearance = document.createElement('div');
      clearance.className = 'bpl-sticky-cart-clearance';
      clearance.setAttribute('aria-hidden', 'true');
      document.body.appendChild(clearance);
    }
    if (clearance) {
      clearance.hidden = !height;
      const value = `${Math.ceil(height)}px`;
      if (clearance.style.height !== value) clearance.style.height = value;
    }
    reserveFloatingSpace(height);
  }

  function reserveFloatingSpace(height) {
    // The existing TrustedSite badge stays visible and clickable above the bar.
    // Only our own body hook/property change; the app's inline style is intact.
    // Body observers watch class only, so these writes do not feed back into sync.
    const body = document.body;
    if (height > 0) {
      const value = `${Math.ceil(height)}px`;
      if (body.style.getPropertyValue('--bpl-sticky-cart-height') !== value) body.style.setProperty('--bpl-sticky-cart-height', value);
      if (!body.hasAttribute('data-bpl-sticky-cart-visible')) body.setAttribute('data-bpl-sticky-cart-visible', '');
    } else {
      if (body.hasAttribute('data-bpl-sticky-cart-visible')) body.removeAttribute('data-bpl-sticky-cart-visible');
      if (body.style.getPropertyValue('--bpl-sticky-cart-height')) body.style.removeProperty('--bpl-sticky-cart-height');
    }
  }

  function schedule() {
    if (!frame) frame = window.requestAnimationFrame(sync);
  }

  function attach(main) {
    if (states.has(main)) return;
    const link = main.querySelector('[data-bpl-dimensions-link]');
    const bar = main.querySelector('[data-bpl-sticky-cart]');
    const form = main.querySelector('[data-product-form]');
    const native = form && form.querySelector('button[name="add"]');
    const button = bar && bar.querySelector('[data-bpl-sticky-add]');
    if (!link && !bar) return;
    let data = {};
    try { data = JSON.parse(form && form.getAttribute('data-product') || '{}'); } catch (_) { /* The native form remains usable. */ }
    const state = { main, link, bar, form, native, button, variants: data.variants || [], observers: [] };
    state.onLink = event => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const target = dimensionsFor(main);
      if (!target) return;
      event.preventDefault();
      goTo(target, target.querySelector('summary') || target);
    };
    if (link) link.addEventListener('click', state.onLink);
    state.onAdd = () => {
      const current = selection(state);
      if (current.mode === 'choose') {
        const option = Array.from(form.querySelectorAll('.single-option-selector, .swatch input[type="radio"], select[name="id"]')).find(visible);
        goTo(form, option || form);
        return;
      }
      if (current.mode !== 'ready') return;
      const nativeForm = native.closest('form');
      if (nativeForm && !nativeForm.checkValidity()) {
        goTo(form, form);
        nativeForm.reportValidity();
        return;
      }
      // One click on the original button: same quantity, variant, properties,
      // validation and native Ajax/redirect behavior. No cart request is made here.
      state.stickySubmitted = true;
      native.click();
      schedule();
    };
    if (bar && native && button) {
      button.addEventListener('click', state.onAdd);
      form.addEventListener('change', schedule, true);
      form.addEventListener('input', schedule, true);
      const observe = (node, options) => {
        const observer = new MutationObserver(schedule);
        observer.observe(node, options);
        state.observers.push(observer);
      };
      observe(native, { attributes: true, childList: true, subtree: true, characterData: true, attributeFilter: ['disabled', 'class', 'aria-busy', 'aria-disabled'] });
      const price = main.querySelector('[data-price-ui], .modal_price');
      if (price) observe(price, { childList: true, subtree: true, characterData: true });
      // Native Ajax errors are inserted after its form; surface them at their
      // existing location after a failed sticky click, without copying messages.
      state.errorObserver = new MutationObserver(records => {
        const warning = records.flatMap(record => Array.from(record.addedNodes)).find(node => node.nodeType === 1 && (node.matches('.warning, .recipient-form--has-errors') || node.querySelector('.warning')));
        if (warning && state.stickySubmitted) { state.stickySubmitted = false; goTo(warning); }
        schedule();
      });
      state.errorObserver.observe(form, { childList: true, subtree: true });
      state.observers.push(state.errorObserver);
      if (window.ResizeObserver) {
        const resize = new ResizeObserver(schedule);
        resize.observe(bar); resize.observe(native);
        state.observers.push(resize);
      }
    } else if (bar) bar.hidden = true;
    states.set(main, state);
  }

  function detach(main) {
    const state = states.get(main);
    if (!state) return;
    if (state.link) { state.link.removeEventListener('click', state.onLink); state.link.hidden = true; }
    if (state.button) state.button.removeEventListener('click', state.onAdd);
    if (state.form) {
      state.form.removeEventListener('change', schedule, true);
      state.form.removeEventListener('input', schedule, true);
    }
    if (state.bar) state.bar.hidden = true;
    state.observers.forEach(observer => observer.disconnect());
    states.delete(main);
    if (!states.size) {
      stopGlobal();
      if (clearance) { clearance.remove(); clearance = null; }
    }
  }

  function startGlobal() {
    if (globalListeners || !states.size) return;
    globalListeners = true;
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    document.addEventListener('click', schedule);
    document.addEventListener('toggle', schedule, true);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule, { passive: true });
    pageObserver = new MutationObserver(schedule);
    pageObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    // These are existing, observable Turbo UI classes, not application state.
    document.querySelectorAll('.cart-container, dialog, [aria-modal]').forEach(node => pageObserver.observe(node, { attributes: true, attributeFilter: ['class', 'open', 'aria-modal', 'aria-hidden'] }));
  }

  function stopGlobal() {
    globalListeners = false;
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    document.removeEventListener('focusin', schedule);
    document.removeEventListener('focusout', schedule);
    document.removeEventListener('click', schedule);
    document.removeEventListener('toggle', schedule, true);
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', schedule);
    if (pageObserver) pageObserver.disconnect();
    if (frame) { window.cancelAnimationFrame(frame); frame = 0; }
    reserveFloatingSpace(0);
  }

  function init() {
    states.forEach((_, main) => { if (!main.isConnected || !main.matches(mainSelector)) detach(main); });
    document.querySelectorAll(mainSelector).forEach(attach);
    startGlobal();
    schedule();
  }

  function unload(root) {
    unloadingRoots.add(root);
    states.forEach((state, main) => {
      if (root === main || root.contains(main)) detach(main);
      else if (state.target && root.contains(state.target)) {
        state.target = null;
        if (state.link) state.link.hidden = true;
      }
    });
    schedule();
  }

  window.BPLProductUX = { init, unload };
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('load', init, { once: true });
})();
